import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Save, ArrowLeft, Upload, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI } from '../services/apiService';

const MAX_DOC_BYTES = 500 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif', 'application/pdf'];

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

const DocUploadChip = ({ doc, onUpload, onRemove, label = 'Upload' }) => (
  <div className="flex items-center gap-1 min-w-0">
    <label className="inline-flex items-center text-xs font-medium text-primary-700 hover:text-primary-800 cursor-pointer shrink-0">
      <Upload className="w-3.5 h-3.5 mr-1" />
      {doc?.name ? 'Replace' : label}
      <input
        type="file"
        accept="image/*,application/pdf,.pdf"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          onUpload(file);
          e.target.value = '';
        }}
      />
    </label>
    {doc?.data && (
      <button
        type="button"
        onClick={onRemove}
        className="text-red-500 hover:text-red-700 p-0.5"
        title="Remove document"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    )}
  </div>
);

const MemberForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    sl_no: '',
    name: '',
    referred_by: '',
    uan: '',
    uan_password: '',
    password: '',
    phone_number: '',
    date_of_birth: '',
    whatsapp: '',
    aadhar_number: '',
    pan_number: '',
    bank_account: '',
    ifsc_code: ''
  });
  const [kycDocuments, setKycDocuments] = useState({});
  const [sendInvite, setSendInvite] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEdit);

  const fetchMember = useCallback(async () => {
    try {
      const response = await membersAPI.getById(id);
      const member = response.member;

      if (member) {
        setFormData({
          sl_no: member.sl_no || '',
          name: member.name || '',
          referred_by: member.referred_by || '',
          uan: member.uan || '',
          uan_password: member.uan_password || '',
          password: member.password || '',
          phone_number: member.phone_number || '',
          date_of_birth: member.date_of_birth
            ? String(member.date_of_birth).slice(0, 10)
            : '',
          whatsapp: member.whatsapp || '',
          aadhar_number: member.aadhar_number || '',
          pan_number: member.pan_number || '',
          bank_account: member.bank_account || '',
          ifsc_code: member.ifsc_code || ''
        });
        setKycDocuments(() => {
          const docs = member.kyc_documents && typeof member.kyc_documents === 'object'
            ? { ...member.kyc_documents }
            : {};
          if (!docs.bank?.data && docs.ifsc?.data) {
            docs.bank = docs.ifsc;
          }
          delete docs.ifsc;
          return docs;
        });
      } else {
        toast.error('Member not found');
        navigate('/adminportal/members');
      }
    } catch (error) {
      console.error('Error fetching member:', error);
      navigate('/adminportal/members');
    } finally {
      setInitialLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (isEdit) {
      fetchMember();
    }
  }, [isEdit, fetchMember]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setSendInvite(checked);
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleDocUpload = async (docKey, file) => {
    if (!file) return;
    const mime = String(file.type || '').toLowerCase();
    if (!ACCEPTED_TYPES.includes(mime)) {
      toast.error('Only image or PDF files are allowed');
      return;
    }
    if (file.size > MAX_DOC_BYTES) {
      toast.error('File must be 500KB or less');
      return;
    }
    try {
      const data = await readFileAsDataUrl(file);
      setKycDocuments((prev) => ({
        ...prev,
        [docKey]: { name: file.name, mime, data }
      }));
      toast.success(`${file.name} attached`);
    } catch (error) {
      console.error(error);
      toast.error('Unable to read file');
    }
  };

  const removeDoc = (docKey) => {
    setKycDocuments((prev) => {
      const next = { ...prev };
      delete next[docKey];
      return next;
    });
  };

  const generateRandomPassword = () => {
    const length = 8;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const sendInviteMessage = (memberData, generatedPassword) => {
    if (!memberData.phone_number && !memberData.whatsapp) {
      toast.error('Phone number or WhatsApp link required to send invite');
      return;
    }

    const portalUrl = window.location.origin;
    const message = `Hello ${memberData.name}!

Welcome to PF Claims Management Portal!

Your login credentials:
👤 Username (UAN): ${memberData.uan}
🔑 Password: ${generatedPassword}

🔗 Portal URL: ${portalUrl}

⚠️ IMPORTANT: You must change your password on first login.

Click the link above to access your portal.

Best regards,
PF Claims Management Team`;

    const encodedMessage = encodeURIComponent(message);
    let cleanPhone = (memberData.phone_number || '').replace(/\D/g, '');

    if (cleanPhone.startsWith('91')) {
      cleanPhone = cleanPhone.substring(2);
    } else if (cleanPhone.startsWith('0')) {
      cleanPhone = cleanPhone.substring(1);
    }

    if (!cleanPhone && memberData.whatsapp) {
      const match = memberData.whatsapp.match(/wa\.me\/91(\d+)/);
      if (match) {
        cleanPhone = match[1];
      }
    }

    if (cleanPhone) {
      const whatsappUrl = `https://wa.me/91${cleanPhone}?text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
      toast.success('WhatsApp invite sent!');
    } else {
      toast.error('Unable to send invite - phone number not available');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let memberData = {
        ...formData,
        date_of_birth: formData.date_of_birth || null,
        uan_password: formData.uan_password || null,
        kyc_documents: kycDocuments
      };
      let generatedPassword = null;

      if (sendInvite) {
        generatedPassword = generateRandomPassword();
        memberData.password = generatedPassword;
        memberData.password_changed = false;
        memberData.password_is_temporary = true;
      } else if (!isEdit && !memberData.password) {
        generatedPassword = generateRandomPassword();
        memberData.password = generatedPassword;
        memberData.password_changed = false;
        memberData.password_is_temporary = false;
      } else if (isEdit && !sendInvite) {
        // Keep existing portal password unless a new one is entered
        if (!String(formData.password || '').trim()) {
          delete memberData.password;
        }
      }

      if (isEdit) {
        const updatedMember = await membersAPI.update(id, memberData);
        toast.success('Member updated successfully');
        if (sendInvite && generatedPassword) {
          setTimeout(() => {
            sendInviteMessage(updatedMember, generatedPassword);
          }, 500);
        }
        navigate('/adminportal/members');
      } else {
        const createdMember = await membersAPI.create(memberData);
        toast.success('Member created successfully');
        if (sendInvite && generatedPassword) {
          setTimeout(() => {
            sendInviteMessage(createdMember, generatedPassword);
          }, 500);
        }
        navigate('/adminportal/members');
      }
    } catch (error) {
      console.error('Error saving member:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error?.message;
      if (msg) toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-3 rounded-t-lg">
          <div className="flex items-center space-x-3">
            <button
              type="button"
              onClick={() => navigate('/adminportal/members')}
              className="text-gray-600 hover:text-gray-700"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <h1 className="text-[1.2rem] sm:text-[1.25rem] font-semibold text-gray-900 leading-snug truncate">
                {isEdit
                  ? `Edit Member - ${formData.name || '—'} - ${formData.uan || '—'}`
                  : 'Add New Member'}
              </h1>
              <p className="text-xs text-gray-500 mt-0.5">
                {isEdit ? 'Update member information' : 'Enter member details · KYC merges to one PDF on Eye preview'}
              </p>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="mb-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Note:</strong> Serial number will be auto-generated and WhatsApp link will be created automatically from phone number.
              KYC uploads accept image/PDF only, max 500KB each.
            </p>
          </div>
          <form id="memberForm" onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="Enter full name"
                />
              </div>

              <div>
                <label htmlFor="referred_by" className="block text-sm font-medium text-gray-700 mb-2">
                  Referred By
                </label>
                <input
                  type="text"
                  id="referred_by"
                  name="referred_by"
                  value={formData.referred_by}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter referrer name"
                />
              </div>

              <div>
                <label htmlFor="uan" className="block text-sm font-medium text-gray-700 mb-2">
                  UAN *
                </label>
                <input
                  type="text"
                  id="uan"
                  name="uan"
                  value={formData.uan}
                  onChange={handleChange}
                  required
                  className="input-field"
                  placeholder="Enter UAN number"
                />
              </div>

              <div>
                <label htmlFor="uan_password" className="block text-sm font-medium text-gray-700 mb-2">
                  UAN Password (Optional)
                </label>
                <input
                  type="text"
                  id="uan_password"
                  name="uan_password"
                  value={formData.uan_password}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter UAN password (optional)"
                />
              </div>

              {isEdit && (
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Portal Password
                  </label>
                  <input
                    type="text"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="input-field"
                    placeholder="Enter new password (leave blank to keep current)"
                  />
                </div>
              )}

              <div>
                <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter phone number"
                />
              </div>

              <div>
                <label htmlFor="date_of_birth" className="block text-sm font-medium text-gray-700 mb-2">
                  DOB
                </label>
                <input
                  type="date"
                  id="date_of_birth"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="input-field"
                />
              </div>

              <div className="md:col-span-2">
                <label htmlFor="whatsapp" className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Link
                </label>
                <input
                  type="url"
                  id="whatsapp"
                  name="whatsapp"
                  value={formData.whatsapp}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Enter WhatsApp link (e.g., //wa.me/9179810)"
                />
              </div>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h2 className="text-sm font-semibold text-slate-800">KYC &amp; Bank details</h2>
                <p className="text-[11px] text-slate-500">
                  One row · Image/PDF max 500KB · Bank+IFSC share one file · List Eye opens merged PDF
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 overflow-x-auto">
                <div className="min-w-[720px] grid grid-cols-4 gap-0 divide-x divide-slate-200">
                  <div className="p-3 min-w-0">
                    <label htmlFor="aadhar_number" className="block text-xs font-medium text-gray-700 mb-1">
                      Aadhar
                    </label>
                    <input
                      type="text"
                      id="aadhar_number"
                      name="aadhar_number"
                      value={formData.aadhar_number}
                      onChange={handleChange}
                      className="input-field !py-1.5 text-sm"
                      placeholder="12-digit Aadhar"
                    />
                    <div className="mt-1.5 flex items-center justify-between gap-1">
                      <DocUploadChip
                        doc={kycDocuments.aadhar}
                        onUpload={(file) => handleDocUpload('aadhar', file)}
                        onRemove={() => removeDoc('aadhar')}
                      />
                      <span className="text-[10px] text-slate-400 truncate max-w-[7rem]" title={kycDocuments.aadhar?.name || ''}>
                        {kycDocuments.aadhar?.name || 'No file'}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 min-w-0">
                    <label htmlFor="pan_number" className="block text-xs font-medium text-gray-700 mb-1">
                      PAN
                    </label>
                    <input
                      type="text"
                      id="pan_number"
                      name="pan_number"
                      value={formData.pan_number}
                      onChange={handleChange}
                      className="input-field !py-1.5 text-sm"
                      placeholder="PAN number"
                    />
                    <div className="mt-1.5 flex items-center justify-between gap-1">
                      <DocUploadChip
                        doc={kycDocuments.pan}
                        onUpload={(file) => handleDocUpload('pan', file)}
                        onRemove={() => removeDoc('pan')}
                      />
                      <span className="text-[10px] text-slate-400 truncate max-w-[7rem]" title={kycDocuments.pan?.name || ''}>
                        {kycDocuments.pan?.name || 'No file'}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 min-w-0 col-span-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="min-w-0">
                        <label htmlFor="bank_account" className="block text-xs font-medium text-gray-700 mb-1">
                          Bank Account
                        </label>
                        <input
                          type="text"
                          id="bank_account"
                          name="bank_account"
                          value={formData.bank_account}
                          onChange={handleChange}
                          className="input-field !py-1.5 text-sm"
                          placeholder="Account number"
                        />
                      </div>
                      <div className="min-w-0">
                        <label htmlFor="ifsc_code" className="block text-xs font-medium text-gray-700 mb-1">
                          IFSC
                        </label>
                        <input
                          type="text"
                          id="ifsc_code"
                          name="ifsc_code"
                          value={formData.ifsc_code}
                          onChange={handleChange}
                          className="input-field !py-1.5 text-sm"
                          placeholder="IFSC code"
                        />
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-1">
                      <DocUploadChip
                        doc={kycDocuments.bank}
                        label="Bank proof"
                        onUpload={(file) => handleDocUpload('bank', file)}
                        onRemove={() => removeDoc('bank')}
                      />
                      <span className="text-[10px] text-slate-400 truncate max-w-[12rem]" title={kycDocuments.bank?.name || ''}>
                        {kycDocuments.bank?.name || 'One file for Bank + IFSC'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-6 py-4 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="sendInvite"
                name="sendInvite"
                checked={sendInvite}
                onChange={handleChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="sendInvite" className="text-sm font-medium text-gray-700">
                Send Invite
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => navigate('/adminportal/members')}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="memberForm"
                disabled={loading}
                className="btn-primary inline-flex items-center"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEdit ? 'Update Member' : 'Create Member'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberForm;
